import type { DataTableRow } from 'carbon-components-svelte/src/DataTable/DataTable.svelte';

import { getDatedCsvFilename } from '../../../shared/csv';
import { rowContainsText } from '../../utils/rowContainsText';
import { downloadCsvStringAsFile } from '../../utils/downloadFile';

import { applyFilters, createRowPipeline } from './client-data';
import { buildCsv, getExportableColumns, type CsvCellFormatter } from './table-csv';
import type { ExportColumnOption, OrderDirection, TableColumn, TableExportPayload } from './table-types';

export type TableQuery = {
  page: number;
  pageSize: number;
  orderBy: string | null;
  orderDirection: OrderDirection;
  searchText: string;
  filters: Record<string, string[]>;
};

/** What the export modal asks for when the user confirms. */
export type ExportRequest = {
  columnKeys: string[];
  includeSearch: boolean;
  filterSelections: Record<string, string[]>;
};

export type ExportContext = { rows: DataTableRow[]; query: TableQuery };

/**
 * A data-source strategy. `Table.svelte` owns UI state and delegates the
 * client-vs-server behavior (row rendering, query commits, export) to one of
 * these, so the event handlers stay mode-agnostic.
 */
export interface TableStrategy {
  readonly mode: 'client' | 'server';
  /** Server sorts remotely, so the view disables client-side header sorting and the component intercepts header clicks. */
  readonly remoteSort: boolean;
  /** Client mode lets Carbon's DataTable slice rows by page/pageSize; server mode renders exactly what the parent provided. */
  readonly clientPaging: boolean;
  /** Rows to render for the current query (client filters/searches in-memory; server returns as-is). */
  displayRows(rows: DataTableRow[], filters: Record<string, string[]>, searchText: string): DataTableRow[];
  /** Called after the query changes; client is a no-op (rendering is reactive), server emits `change`. */
  onQueryChange(query: TableQuery): void;
  /** Runs an export; client builds and downloads the CSV, server emits `export` for the parent to fulfil. */
  runExport(request: ExportRequest, context: ExportContext): void;
}

export type ClientStrategyDeps = {
  getExportConfig: () => {
    headers: TableColumn[];
    exportColumns: ExportColumnOption[] | null;
    csvFilename: string;
    formatCsvCell: CsvCellFormatter;
  };
};

export const createClientStrategy = ({ getExportConfig }: ClientStrategyDeps): TableStrategy => {
  const pipeline = createRowPipeline();

  return {
    mode: 'client',
    remoteSort: false,
    clientPaging: true,
    displayRows: (rows, filters, searchText) => pipeline(rows, filters, searchText),
    onQueryChange: () => {},
    runExport: ({ columnKeys, includeSearch, filterSelections }, { rows, query }) => {
      const { headers, exportColumns, csvFilename, formatCsvCell } = getExportConfig();
      const columns = exportColumns ?? getExportableColumns(headers);
      const filtered = applyFilters(rows, filterSelections);
      const searched = includeSearch && query.searchText ? filtered.filter((row) => rowContainsText(row, query.searchText)) : filtered;
      downloadCsvStringAsFile(getDatedCsvFilename(csvFilename), buildCsv(columns, searched, columnKeys, formatCsvCell));
    },
  };
};

export type ServerStrategyDeps = {
  emitChange: (query: TableQuery) => void;
  emitExport: (payload: TableExportPayload) => void;
};

export const createServerStrategy = ({ emitChange, emitExport }: ServerStrategyDeps): TableStrategy => ({
  mode: 'server',
  remoteSort: true,
  clientPaging: false,
  displayRows: (rows) => rows,
  onQueryChange: (query) => emitChange(query),
  runExport: ({ columnKeys, includeSearch, filterSelections }, { query }) => {
    emitExport({
      columnKeys,
      searchText: includeSearch ? query.searchText : null,
      orderBy: query.orderBy,
      orderDirection: query.orderDirection,
      filters: filterSelections,
    });
  },
});
